import { Injectable } from '@angular/core';

import { XYSeries } from '@amcharts/amcharts5/xy';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { annotatableBlockTypes } from '../../../../Common/Reports/models/new-data-processing-workflow-block-types.model';
import { Am5Chart } from '../../../../Common/Charts/models/am5-chart.model';
import { Am5ChartTooltipService } from '../../../../Common/Charts/services/am5-chart-tooltip.service';
import { Am5DateValueXYChart } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { ChartAnnotationIndicatorTypesEnum } from './chart-annotation-marker-types.enum';
import { ChartBulletTypesEnum } from '../../../../Common/Reports/enums/chart-bullet-types.enum';
import { ChartMetricsEnum } from '../../../../Common/Reports/enums/chart-metrics.enum';
import { ChartSeries } from '../../../../Common/Charts/models/chart-series.model';
import { ChartSpecificTypesEnum } from '../../../../Common/Reports/enums/chart-specific-types.enum';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { MultiSelectDropdownOptionGroup, MultiSelectDropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { ReportBlockTypeEnum } from '../../../../Common/Reports/enums/report-block-type.enum';



@Injectable()
export class ChartAnnotationIndicatorsService {

    constructor(
        public localizationService: LocalizationService,
        public am5ChartTooltipService: Am5ChartTooltipService
    ) {

    }

    getIndicatorGroups(chart: Am5Chart): MultiSelectDropdownOptionGroup[] {
        let groups: MultiSelectDropdownOptionGroup[];
        switch (chart.config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                groups = [
                    {
                        label: this.localizationService.transform(`Reports.annotation.${ChartAnnotationIndicatorTypesEnum.AVERAGE}`),
                        expanded: false,
                        type: ChartAnnotationIndicatorTypesEnum.AVERAGE,
                        options: []
                    },
                    {
                        label: this.localizationService.transform(`Reports.annotation.${ChartAnnotationIndicatorTypesEnum.TREND}`),
                        expanded: false,
                        type: ChartAnnotationIndicatorTypesEnum.TREND,
                        options: []
                    }
                ];
                chart.series.filter(x => !x.indicatorTargetIndex && x.indicatorTargetIndex !== 0).forEach(x => {
                    groups.forEach(g => {
                        const option = new MultiSelectDropdownOptionModel(
                            this.getMetricLabel(chart, x.key),
                            {
                                indicatorType: g.type,
                                metric: x.key
                            },
                            chart.settings.chartAnnotations?.indicators?.find(indicator => indicator.indicatorType === g.type && indicator.metric === x.key)
                        );
                        g.options.push(option);
                    });
                });
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                groups = [
                    {
                        label: this.localizationService.transform(`Reports.annotation.${ChartAnnotationIndicatorTypesEnum.AVERAGE}`),
                        expanded: false,
                        type: ChartAnnotationIndicatorTypesEnum.AVERAGE,
                        options: []
                    },
                    {
                        label: this.localizationService.transform(`Reports.annotation.${ChartAnnotationIndicatorTypesEnum.TREND}`),
                        expanded: false,
                        type: ChartAnnotationIndicatorTypesEnum.TREND,
                        options: []
                    }
                ];
                chart.series.filter(x => !x.indicatorTargetIndex && x.indicatorTargetIndex !== 0).forEach(x => {
                    groups.forEach(g => {
                        const option = new MultiSelectDropdownOptionModel(
                            x.label,
                            {
                                indicatorType: g.type,
                                source: x.label,
                                metric: x.key
                            },
                            chart.settings.chartAnnotations?.indicators?.find(indicator => indicator.indicatorType === g.type && indicator.source === x.key)
                        );
                        g.options.push(option);
                    });
                });
                break;
            default:
                groups = [];
        }

        return groups;
    }

    getMetricLabel(chart: Am5Chart, metric: string): string {
        if (chart.config.type === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND && metric !== ChartMetricsEnum.volumeOfMentions) {
            return this.localizationService.transform(`Reports.googleAnalyticsCharts.${metric}`);
        }

        return this.localizationService.transform(`Reports.formField.analysis.${metric}`);
    }

    updateIndicators(chart: Am5Chart, nonIndicatorChartSeries: ChartSeries[], indicators: any[]): void {
        const indicatorSeries = this.getChartSeriesFromIndicators(chart, indicators, nonIndicatorChartSeries.length - 1);
        chart.series = [...nonIndicatorChartSeries, ...indicatorSeries];
        let c;
        switch (chart.config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                c = chart as Am5DateValueXYChart;
                c.amchart?.series.clear();
                c.setUpSeries(chart.series);
                c.series.forEach(x => x.grouped = false);
                if (c.legend) {
                    c.legend.data.setAll(c.amchart.series.values.slice(0, nonIndicatorChartSeries.length));
                }
                break;
        }
    }

    getChartSeriesFromIndicators(chart: Am5Chart, indicators: any[], currentChartSeriesIndex: number): ChartSeries[] {
        const chartSeries = chart.series;
        const indicatorChartSeries: ChartSeries[] = [];
        indicators.forEach(indicator => {
            currentChartSeriesIndex++;
            let chartSeriesIndex = -1;
            switch (chart.config.type) {
                case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                    chartSeriesIndex = chartSeries.findIndex(x => x.key === indicator.metric);
                    break;
                case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                    chartSeriesIndex = chartSeries.findIndex(x => x.label === indicator.source);
                    break;
            }
            if (chartSeriesIndex === -1) {
                return;
            }
            const cs = chartSeries[chartSeriesIndex];
            const series = {} as ChartSeries;
            series.indicatorTargetIndex = chartSeriesIndex;
            series.requiresAxis = false;
            series.axisIndex = cs.axisIndex;
            series.colorMapKey = 'analysisColor' + (chartSeriesIndex + 1);
            series.strokeDasharray = [10, 5];
            series.tooltipHTML = this.am5ChartTooltipService.getIndicatorTooltip(indicator, currentChartSeriesIndex, chart, chartSeries[chartSeriesIndex]);
            series.getSeriesUrl = (model: any) => '';
            switch (chart.config.type) {
                case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                    series.chartSpecificType = ChartSpecificTypesEnum.LINE;
                    series.key = indicator.metric;
                    series.colorMapKey = 'analysisColor' + (chartSeriesIndex + 1);
                    break;
                case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                    series.chartSpecificType = ChartSpecificTypesEnum.LINE;
                    series.key = indicator.source;
                    series.colorMapKey = 'seriesColor' + chartSeriesIndex;
                    break;
                case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                    series.chartSpecificType = ChartSpecificTypesEnum.LINE;
                    series.key = indicator.metric;
                    series.colorMapKey = cs.colorMapKey;
                    break;
            }
            switch (indicator.indicatorType) {
                case ChartAnnotationIndicatorTypesEnum.AVERAGE:
                    series.calculateIndicators = this.calculateAverage;
                    break;
                case ChartAnnotationIndicatorTypesEnum.TREND:
                    series.calculateIndicators = this.calculateTrend;
                    series.showLastItem = true;
                    series.bulletType = ChartBulletTypesEnum.RIGHT_ARROW;
                    break;
            }
            indicatorChartSeries.push(series);
        });

        return indicatorChartSeries;
    }

    calculateAverage(data: DataPointModel[], targetValueKey: string, indicatorValueKey: string): void {
        const targetValues = data.map(x => x[targetValueKey]) as number[];

        const n = targetValues.length;
        if (n < 2) {
            return null;
        }
        const average = targetValues.reduce((x, y) => x + y, 0) / n;

        data[0][indicatorValueKey] = average;
        data[data.length - 1][indicatorValueKey] = average;
    }

    calculateTrend(data: DataPointModel[], targetValueKey: string, indicatorValueKey: string): void {
        const points = data.map(d => {
            return { x: d.key as number, y: d[targetValueKey] as number };
        });

        const n = points.length;
        if (n < 2) {
            return null;
        }
        let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
        for (const { x, y } of points) {
            sumX += x;
            sumY += y;
            sumXY += x * y;
            sumXX += x * x;
        }
        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        data[0][indicatorValueKey] = slope * (data[0].key as number) + intercept;
        data[data.length - 1][indicatorValueKey] = slope * (data[data.length - 1].key as number) + intercept;
    }

    calculateIndicatorsForGroupedData(chart: Am5Chart): void {
        if (annotatableBlockTypes.includes(chart.config.type)) {
            if (!chart.series.some(x => x.calculateIndicators)) {
                return;
            }
            chart.amchart?.series.each((s, seriesIndex) => {
                (s as XYSeries).events.on('datasetchanged', (event) => {
                    const originalData = (event.target as XYSeries).mainDataItems;
                    const dataItems = event.target.dataItems;
                    if (!dataItems.length) {
                        return;
                    }
                    const groups = dataItems.map(dataItem => {
                        return {
                            groupedDataPoint: dataItem.dataContext as DataPointModel,
                            originalDataPoints: (dataItem.get('originals') || [dataItem]).map(x => x.dataContext as DataPointModel)
                        };
                    });
                    chart.series.forEach((chartSeries, chartSeriesIndex) => {
                        if (!chartSeries.calculateIndicators || chartSeries.grouped) {
                            return;
                        }
                        const targetValueKey = 'value' + chartSeries.indicatorTargetIndex;
                        const indicatorValueKey = 'value' + chartSeriesIndex;
                        // recalculate grouped values
                        groups.forEach(group => {
                            group.groupedDataPoint[targetValueKey] = group.originalDataPoints.reduce(
                                (sum, dataPoint) => sum + ((dataPoint[targetValueKey] as number) || 0), 0
                            );
                        });
                        chartSeries.calculateIndicators(groups.map(x => x.groupedDataPoint), targetValueKey, indicatorValueKey);
                        groups.forEach(group => {
                            // calculate split data to assign to original data, and let am5 group
                            const ungroupedValue = (group.groupedDataPoint[indicatorValueKey] as number) / group.originalDataPoints.length;
                            group.originalDataPoints.forEach(dataPoint => dataPoint[indicatorValueKey] = ungroupedValue);
                        });
                        (chart as Am5DateValueXYChart).updateIndicatorData(JSON.parse(JSON.stringify(originalData.map(x => x.dataContext))));
                        chartSeries.grouped = true;
                    });
                });
            });
        }
    };
}
